import type { ReactNode, KeyboardEvent, ClipboardEvent } from "react";
import type { IconSvgElement } from "@hugeicons/react";

export enum InputSize {
  SM = "SM",
  BASE = "BASE"
}

export interface InputParser<TExternal> {
  inputMode: "text" | "numeric" | "decimal" | "tel";
  validate(raw: string): boolean;
  parse(raw: string): TExternal;
  format(value: TExternal): string;
  toEditable(value: TExternal): string;
  cleanup?(raw: string): string;
}

interface BaseInputCommon {
  id: string;
  name?: string;

  label?: string | ReactNode;
  labelClassName?: string;
  required?: boolean;
  hideOptional?: boolean;
  optionalText?: string;

  size?: InputSize;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  prefix?: string;
  suffix?: string;
  Icon?: IconSvgElement;
  ErrorIcon?: IconSvgElement;
  iconClassName?: string;

  error?: string | boolean;
  hideErrorMessage?: boolean;
  readOnly?: boolean;
  disabled?: boolean;

  autoFocus?: boolean;
  focusOnError?: boolean;
  scrollIntoViewOnFocus?: boolean;
  autoComplete?: string;
  maxLength?: number;

  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void;
}

interface WithParser<T> {
  parser: InputParser<T>;
  value: T;
  onChange: (value: T) => void;
  type?: never;
}

interface WithoutParser {
  parser?: undefined;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "password" | "email" | "search" | "url" | "time";
}

export type BaseInputProps<T = string> = BaseInputCommon &
  (WithParser<T> | WithoutParser);
